import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { EndPoints } from "../../../Constants/endpoints";
import { MedicalFields, MedicalKeys } from "../../../enums/MedicalReportEnums";
import usePostData from "../../../hooks/ReactQuery/usePostData";
import { IStepFields } from "../../../interface/MedicalRecord";
import { useToast } from "../../../Services/ToastContext";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { resetFileCount } from "../../../redux/reducers/investigationFilesSlice";
import { cleanEmptyValues } from "../utils";

interface useMedicalRecordDataProps {
  methods: any;
  resetSteps: () => void;
}
export const useMedicalRecordData = ({
  methods,
  resetSteps,
}: useMedicalRecordDataProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { PatientId, templateId } = useParams();
  const dispatch = useDispatch()

  const gender = methods.watch(MedicalKeys.Gender);

  useEffect(() => {
    methods.clearErrors(MedicalKeys.IsPregnancy);
    gender?.toLowerCase() === "male" &&
      methods.setValue(MedicalKeys.IsPregnancy, "false");
  }, [gender]);

  const createRecordMutation = usePostData(EndPoints.MedicalRecordCreate);

  const updateOrCreateRecord = (formData: any) => {
    const filteredData = Object.fromEntries(
      Object.entries(formData).filter(([key]) => !key.includes("-option")),
    );

    // const cleanedValues = cleanEmptyValues(filteredData);

    // console.log("cleanedValues",filteredData,cleanedValues)

    const formattedData = {
      MedicalHistoryTypeId: templateId,
      ...filteredData,
    };
    return createRecordMutation.mutate(formattedData, {
      onSuccess: () => {
        showToast(t("success"), "success");
        dispatch(resetFileCount());
        queryClient.refetchQueries({
          queryKey: [EndPoints.GetAllVisit],
          exact: false,
        });

        queryClient.refetchQueries({
          queryKey: [EndPoints.GetMedicalRecord],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [EndPoints.GetAllPatientMedicalRecords],
          exact: false,
        });
        queryClient.refetchQueries({
          queryKey: [EndPoints.GetPrescriptionToPatient + "/" + PatientId],
        });

        methods.reset();
        resetSteps();
        navigate(
          `/patients_list/${PatientId}/medical-records?openAppointment=true`,
        );
      },
      onError: (error: any) => showToast(error.message || t("failed"), "error"),
    });
  };

  const getFieldValue = (field: IStepFields) =>
    field?.Value ??
    field?.InverseParentStepFields?.find(
      (option) =>
        option?.Type === MedicalFields.Option && option?.Selected === true,
    )?.Id;

  const collectAttributes = (fields: IStepFields[], Attributes: any) => {
    fields.forEach((field) => {
      if (field?.Type !== MedicalFields.Option) {
        Attributes[`${field?.Id}`] = getFieldValue(field);
      }

      if (field?.InverseParentStepFields?.length) {
        collectAttributes(field.InverseParentStepFields, Attributes);
      }
    });

    return Attributes;
  };


  return {
    createRecordMutation,
    updateOrCreateRecord,
  };
};
